import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../components/molecules/SearchBar";
import ComponentCard from "../components/organisms/ComponentCard";
import { getSavedComponents, getPrivateComponents, deleteSavedComponent, deletePrivateComponent } from "../api/libraryService";
import { publishComponent } from "../api/componentService";
import { getUserRole } from "../utils/auth";
import Button from "../components/atoms/Button";
import { useToast } from "../components/atoms/Toast";

const Library = () => {
  const navigate = useNavigate();
  const [savedComponents, setSavedComponents] = useState([]);
  const [privateComponents, setPrivateComponents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isAdmin = getUserRole() === 'ADMIN';
  const toast = useToast();

  const loadLibrary = useCallback(async () => {
    try {
      setLoading(true);
      const [savedRes, privateRes] = await Promise.all([
        getSavedComponents(),
        getPrivateComponents()
      ]);
      setSavedComponents(savedRes.data);
      setPrivateComponents(privateRes.data);
      setError(null);
    } catch (err) {
      setError("Failed to load your library.");
      toast?.error?.('Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const filteredSaved = savedComponents.filter((component) =>
    component.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    component.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredPrivate = privateComponents.filter((component) =>
    component.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    component.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearch = (value) => setSearchQuery(value);
  const handleView = (id) => navigate(`/components/view/${id}`);
  const handleEdit = (id) => navigate(`/components/edit/${id}`);
  const handleDeleteSaved = async (id) => {
    const ok = await (toast?.confirm ? toast.confirm('Remove this from your saved list?') : Promise.resolve(true));
    if (!ok) return;
    try {
      await deleteSavedComponent(id);
      toast?.success?.('Removed from saved');
      await loadLibrary();
    } catch (e) {
      toast?.error?.('Failed to remove saved');
    }
  };
  const handleDeletePrivate = async (id) => {
    const ok = await (toast?.confirm ? toast.confirm('Delete this private component?') : Promise.resolve(true));
    if (!ok) return;
    try {
      await deletePrivateComponent(id);
      toast?.success?.('Private component deleted');
      await loadLibrary();
    } catch (e) {
      toast?.error?.('Failed to delete private component');
    }
  };
  const handleAddPrivate = () => navigate(`/components/create${isAdmin ? '?private=1' : '?private=1'}`);
  const handlePublish = async (id) => {
    try {
      await publishComponent(id);
      toast?.success?.('Published to public');
      await loadLibrary();
    } catch (e) {
      toast?.error?.('Failed to publish');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">My Library</h1>
        <p className="text-gray-600 mb-6">
          Your saved and private components are listed here. Only you can see your
          private components.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-8">
          <div className="flex-1 max-w-md">
            <SearchBar
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search your components..."
            />
          </div>
          <Button onClick={handleAddPrivate} variant="primary">
            Add Private Component
          </Button>
        </div>
        {!isAdmin && (
          <section className="mb-12">
            <h2 className="text-xl font-semibold mb-3">Saved Components</h2>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">{error}</div>
            ) : filteredSaved.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSaved.map((component) => (
                  <ComponentCard
                    key={component.id}
                    component={component}
                    onView={handleView}
                    onDelete={handleDeleteSaved}
                    onEdit={handleEdit}
                    isSaved
                  />
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-8">
                No saved components found.
              </div>
            )}
          </section>
        )}
        <section>
          <h2 className="text-xl font-semibold mb-3">Private Components</h2>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : filteredPrivate.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPrivate.map((component) => (
                <ComponentCard
                  key={component.id}
                  component={component}
                  onView={handleView}
                  onDelete={handleDeletePrivate}
                  onEdit={handleEdit}
                  isPrivate
                  canManage
                  extraActions={isAdmin ? (
                    <Button size="sm" variant="primary" onClick={() => handlePublish(component.id)}>
                      Publish
                    </Button>
                  ) : null}
                />
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">
              No private components found.
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Library;
